export interface ThirdPartyFee {
  name: string;
  value: number;
}

export type LoanType = "refinance" | "purchase";
export type Lender = "provident" | "rocket";

/**
 * Calculate third party fees for a loan
 * @param loanAmount - The loan amount in dollars
 * @param loanType - The type of loan (refinance or purchase)
 * @param lender - The lender (provident or rocket)
 * @returns Array of third party fees
 */
export function calculateThirdPartyFees(
  loanAmount: number,
  loanType: LoanType,
  lender: Lender,
): ThirdPartyFee[] {
  const fees: ThirdPartyFee[] = [
    {
      name: "Appraisal",
      value: 700,
    },
    {
      name: "Title Estimate",
      value: calculateTitleEstimate(loanAmount, loanType, lender),
    },
  ];

  return fees;
}

export function calculateThirdPartyFeesAmount(fees: ThirdPartyFee[]): number {
  return fees.reduce((acc, fee) => acc + fee.value, 0);
}

/**
 * Calculate title estimate based on loan amount, loan type, and lender
 * - Rocket + refinance: Flat $1,165
 * - Rocket + purchase: Provident calculation (policy + closing fee + recording)
 * - Provident (any loan type): Provident calculation
 * @param loanAmount - The loan amount in dollars
 * @param loanType - The type of loan (refinance or purchase)
 * @param lender - The lender (provident or rocket)
 * @returns Title estimate in dollars
 */
export function calculateTitleEstimate(
  loanAmount: number,
  loanType: LoanType,
  lender: Lender,
): number {
  // Rocket + refinance uses flat $1,165
  if (lender === "rocket" && loanType === "refinance") {
    return 1165;
  }

  // All other cases use Provident calculation (policy + closing fee + recording)
  const policyAmount = calculateTitlePolicy(loanAmount, loanType);
  const closingFee = 370;
  const recordingFee = 80;

  return policyAmount + closingFee + recordingFee;
}

/**
 * Calculate title policy amount based on loan amount and loan type
 * @param loanAmount - The loan amount in dollars
 * @param loanType - The type of loan (refinance or purchase)
 * @returns Title policy amount in dollars
 */
function calculateTitlePolicy(loanAmount: number, loanType: LoanType): number {
  // Meridian Title Policies
  const titlePolicyRates = {
    100000: { refi: 423, buyers: 489, sellers: 715 },
    110000: { refi: 453, buyers: 524, sellers: 767 },
    120000: { refi: 484, buyers: 558, sellers: 819 },
    130000: { refi: 514, buyers: 594, sellers: 871 },
    140000: { refi: 544, buyers: 629, sellers: 921 },
    150000: { refi: 575, buyers: 664, sellers: 973 },
    160000: { refi: 605, buyers: 699, sellers: 1025 },
    170000: { refi: 636, buyers: 734, sellers: 1076 },
    180000: { refi: 666, buyers: 769, sellers: 1127 },
    190000: { refi: 696, buyers: 805, sellers: 1179 },
    200000: { refi: 727, buyers: 839, sellers: 1230 },
    210000: { refi: 751, buyers: 867, sellers: 1271 },
    220000: { refi: 775, buyers: 895, sellers: 1313 },
    230000: { refi: 800, buyers: 923, sellers: 1353 },
    240000: { refi: 824, buyers: 951, sellers: 1394 },
    250000: { refi: 848, buyers: 979, sellers: 1436 },
    260000: { refi: 873, buyers: 1007, sellers: 1477 },
    270000: { refi: 897, buyers: 1036, sellers: 1518 },
    280000: { refi: 921, buyers: 1063, sellers: 1559 },
    290000: { refi: 946, buyers: 1092, sellers: 1600 },
    300000: { refi: 970, buyers: 1119, sellers: 1642 },
    310000: { refi: 994, buyers: 1148, sellers: 1682 },
    320000: { refi: 1018, buyers: 1175, sellers: 1724 },
    330000: { refi: 1043, buyers: 1204, sellers: 1765 },
    340000: { refi: 1067, buyers: 1231, sellers: 1806 },
    350000: { refi: 1091, buyers: 1260, sellers: 1847 },
    360000: { refi: 1116, buyers: 1287, sellers: 1888 },
    370000: { refi: 1140, buyers: 1316, sellers: 1930 },
    380000: { refi: 1164, buyers: 1344, sellers: 1971 },
    390000: { refi: 1189, buyers: 1372, sellers: 2011 },
    400000: { refi: 1213, buyers: 1400, sellers: 2053 },
    410000: { refi: 1237, buyers: 1428, sellers: 2094 },
    420000: { refi: 1262, buyers: 1456, sellers: 2136 },
    430000: { refi: 1286, buyers: 1484, sellers: 2176 },
    440000: { refi: 1310, buyers: 1512, sellers: 2217 },
    450000: { refi: 1335, buyers: 1540, sellers: 2259 },
    460000: { refi: 1359, buyers: 1568, sellers: 2299 },
    470000: { refi: 1383, buyers: 1597, sellers: 2341 },
    480000: { refi: 1407, buyers: 1624, sellers: 2382 },
    490000: { refi: 1432, buyers: 1653, sellers: 2423 },
    500000: { refi: 1456, buyers: 1680, sellers: 2464 },
    510000: { refi: 1468, buyers: 1694, sellers: 2485 },
    520000: { refi: 1480, buyers: 1709, sellers: 2505 },
    530000: { refi: 1493, buyers: 1723, sellers: 2526 },
    540000: { refi: 1505, buyers: 1736, sellers: 2547 },
    550000: { refi: 1517, buyers: 1751, sellers: 2567 },
    560000: { refi: 1529, buyers: 1765, sellers: 2588 },
    570000: { refi: 1541, buyers: 1779, sellers: 2609 },
    580000: { refi: 1553, buyers: 1792, sellers: 2628 },
    590000: { refi: 1565, buyers: 1807, sellers: 2649 },
    600000: { refi: 1578, buyers: 1821, sellers: 2670 },
    610000: { refi: 1590, buyers: 1835, sellers: 2691 },
    620000: { refi: 1602, buyers: 1848, sellers: 2711 },
    630000: { refi: 1614, buyers: 1863, sellers: 2732 },
    640000: { refi: 1626, buyers: 1877, sellers: 2753 },
    650000: { refi: 1638, buyers: 1891, sellers: 2772 },
    660000: { refi: 1651, buyers: 1905, sellers: 2793 },
    670000: { refi: 1663, buyers: 1919, sellers: 2814 },
    680000: { refi: 1675, buyers: 1933, sellers: 2834 },
    690000: { refi: 1687, buyers: 1947, sellers: 2855 },
    700000: { refi: 1699, buyers: 1961, sellers: 2876 },
    710000: { refi: 1711, buyers: 1975, sellers: 2897 },
    720000: { refi: 1723, buyers: 1989, sellers: 2917 },
    730000: { refi: 1736, buyers: 2004, sellers: 2937 },
    740000: { refi: 1748, buyers: 2017, sellers: 2958 },
    750000: { refi: 1760, buyers: 2031, sellers: 2978 },
    760000: { refi: 1772, buyers: 2045, sellers: 2999 },
  };

  // Find the appropriate policy rate by rounding up to the nearest bracket
  const policyBrackets = Object.keys(titlePolicyRates)
    .map(Number)
    .sort((a, b) => a - b);

  // Find the first bracket that's >= loan amount
  const applicableBracket = policyBrackets.find(
    (bracket) => bracket >= loanAmount,
  );

  if (applicableBracket) {
    const rates =
      titlePolicyRates[applicableBracket as keyof typeof titlePolicyRates];

    switch (loanType) {
      case "refinance":
        return rates.refi;
      case "purchase":
        // For now, use buyers rate until we determine which one to use
        return rates.buyers;
      default:
        return rates.refi;
    }
  }

  // If loan amount exceeds max bracket, use the highest available rate
  const maxBracket = Math.max(...policyBrackets);
  const maxRates =
    titlePolicyRates[maxBracket as keyof typeof titlePolicyRates];

  switch (loanType) {
    case "refinance":
      return maxRates.refi;
    case "purchase":
      return maxRates.buyers;
    default:
      return maxRates.refi;
  }
}
